import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Input, Select, Button, Table, Pagination, Row, Col, DatePicker, message, Modal, Spin, ConfigProvider, Card, Descriptions, Badge, Space, Form, Upload, Tabs, Tag, Tooltip } from 'antd';
import { UnorderedListOutlined, ReloadOutlined, UploadOutlined, SyncOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Plus, MagnifyingGlass, FunnelSimple, Eye } from '@phosphor-icons/react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, get, set, push, onValue, off } from 'firebase/database';
import { useFirebase } from '../FirebaseContext'; // Используем контекст вместо прямого импорта базы
import moment from 'moment';
import 'moment/locale/pl'; // Подключаем локаль moment.js для польского языка
import plPL from 'antd/es/locale/pl_PL'; // Импортируем польскую локализацию для Ant Design
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import './Faktury.css';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import isoWeek from 'dayjs/plugin/isoWeek';
import KsefInvoiceView from './KsefInvoiceView';

const { Option } = Select;
const { RangePicker } = DatePicker;

moment.updateLocale('pl', {
  week: {
    dow: 1, // Устанавливаем понедельник первым днем недели
    doy: 4, // Первая неделя года начинается с 4 января (ISO 8601)
  },
});

const Faktury = () => {
  // Получаем текущий экземпляр Firebase через контекст
  const { currentFirebase } = useFirebase();
  const db = currentFirebase.database; // Используем актуальную базу данных
  const userEmail = currentFirebase.auth?.currentUser?.email?.toLowerCase() || '';

  // KSeF invoice preview modal
  const [ksefViewInvoice, setKsefViewInvoice] = useState(null);
  const [isKsefViewModalVisible, setIsKsefViewModalVisible] = useState(false);

  // KSeF access: load from Firebase
  const [hasKsefAccess, setHasKsefAccess] = useState(false);

  useEffect(() => {
    const accessRef = ref(db, 'ksefAccessEmails');
    const unsub = onValue(accessRef, (snapshot) => {
      if (snapshot.exists()) {
        const emails = Object.values(snapshot.val()).map(e => e.toLowerCase());
        setHasKsefAccess(emails.includes(userEmail));
      } else {
        setHasKsefAccess(false);
      }
    });
    return () => unsub();
  }, [db, userEmail]);

  const [searchValue, setSearchValue] = useState('');
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');
  const [statuses, setStatuses] = useState([]);
  const [types, setTypes] = useState([]);

  // Устанавливаем по умолчанию последние 8 дней
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(8, 'days').startOf('day'),
    dayjs().endOf('day')
  ]);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [allInvoicesData, setAllInvoicesData] = useState([]); // ВСЕ фактуры из БД (загружаются 1 раз)
  const [invoicesData, setInvoicesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sorterInfo, setSorterInfo] = useState({});

  // Новые состояния для очереди задач
  const [isQueueModalVisible, setIsQueueModalVisible] = useState(false);
  const [queueData, setQueueData] = useState(null);
  const [queueLoading, setQueueLoading] = useState(false);

  // Новые состояния для добавления фактур
  const [isAddInvoiceModalVisible, setIsAddInvoiceModalVisible] = useState(false);
  const [addInvoiceForm] = Form.useForm();
  const [driversData, setDriversData] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [isInvoiceDetailsModalVisible, setIsInvoiceDetailsModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);

  // KSeF state
  const [activeTab, setActiveTab] = useState('faktury');
  const [ksefInvoices, setKsefInvoices] = useState([]);
  const [ksefAllInvoices, setKsefAllInvoices] = useState([]);
  const [ksefLoading, setKsefLoading] = useState(false);
  const [ksefSyncLoading, setKsefSyncLoading] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [ksefSearch, setKsefSearch] = useState('');
  const [ksefTransferModalVisible, setKsefTransferModalVisible] = useState(false);
  const [ksefTransferInvoice, setKsefTransferInvoice] = useState(null);
  const [ksefTransferLoading, setKsefTransferLoading] = useState(false);
  const [ksefTransferForm] = Form.useForm();
  const [ksefDriversList, setKsefDriversList] = useState([]);
  const [ksefDriversLoaded, setKsefDriversLoaded] = useState(false);

  // Presety okresu liczone przy renderze, żeby nie zastygały w długo otwartej karcie.
  const rangePresets = useMemo(() => [
    { label: '7 dni', value: [dayjs().subtract(7, 'days').startOf('day'), dayjs().endOf('day')] },
    { label: 'Ostatni miesiąc', value: [dayjs().subtract(1, 'month').startOf('day'), dayjs().endOf('day')] },
    { label: 'Bieżący miesiąc', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
  ], []);

  // Функция для форматирования типа документа
  const formatDocumentType = (type) => {
    if (!type) return '-';

    const typeMapping = {
      'faktura': 'FV',
      'Faktura': 'FV',
      'FAKTURA': 'FV',
      'paragon': 'PR',
      'Paragon': 'PR',
      'PARAGON': 'PR'
    };

    const mappedType = typeMapping[type] || type;

    // Ограничиваем до 3 символов
    return mappedType.length > 3 ? mappedType.substring(0, 3) : mappedType;
  };

  // Функция для форматирования даты
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';

    // Если дата в формате YYYY-MM-DD
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return moment(dateValue, 'YYYY-MM-DD').format('DD.MM.YYYY');
    }

    // Если дата в формате DD-MM-YYYY
    if (typeof dateValue === 'string' && dateValue.includes('-') && dateValue.length === 10) {
      const parts = dateValue.split('-');
      if (parts[0].length === 2) { // DD-MM-YYYY
        return moment(dateValue, 'DD-MM-YYYY').format('DD.MM.YYYY');
      } else { // YYYY-MM-DD
        return moment(dateValue, 'YYYY-MM-DD').format('DD.MM.YYYY');
      }
    }

    // Если дата уже в формате DD.MM.YYYY
    if (typeof dateValue === 'string' && dateValue.includes('.')) {
      return dateValue;
    }

    // Попытка парсинга как timestamp или другой формат
    const momentDate = moment(dateValue);
    if (momentDate.isValid()) {
      return momentDate.format('DD.MM.YYYY');
    }

    return dateValue;
  };

  // Загрузка данных из Firebase — ТОЛЬКО при смене проекта (db)
  useEffect(() => {
    loadAndDisplayData();
  }, [db]);

  // Фильтрация по дате через useMemo — без повторного запроса к БД
  const dateFilteredData = useMemo(() => {
    if (!dateRange || !allInvoicesData.length) return allInvoicesData;
    const startTimestamp = dateRange[0].clone().startOf('day').valueOf();
    const endTimestamp = dateRange[1].clone().endOf('day').valueOf();
    return allInvoicesData.filter(invoice => {
      const ts = typeof invoice.timestamp === 'number' ? invoice.timestamp : new Date(invoice.timestamp).getTime();
      return ts >= startTimestamp && ts <= endTimestamp;
    });
  }, [allInvoicesData, dateRange]);

  // Обновляем invoicesData при изменении dateFilteredData
  useEffect(() => {
    setInvoicesData(dateFilteredData);
    setCurrentPage(1);
  }, [dateFilteredData]);

  // Фильтрация по поиску/статусу/типу через useMemo
  const computedFilteredData = useMemo(() => {
    const lowerSearch = searchValue.toLowerCase();
    return dateFilteredData.filter(invoice => {
      const matchesSearch = !lowerSearch || (
        (invoice.driverId?.toLowerCase().includes(lowerSearch)) ||
        (invoice.numerfaktury?.toLowerCase().includes(lowerSearch)) ||
        (invoice.nipseller?.toLowerCase().includes(lowerSearch)) ||
        (invoice.rejectionComment?.toLowerCase().includes(lowerSearch))
      );
      const matchesStatus = status === 'all' || invoice.status === status;
      const matchesType = type === 'all' || invoice.type === type;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [dateFilteredData, searchValue, status, type]);

  // Синхронизируем filteredData со state для обратной совместимости
  useEffect(() => {
    setFilteredData(computedFilteredData);
    setCurrentPage(1);
  }, [computedFilteredData]);

  // Load drivers list once for KSeF tab
  useEffect(() => {
    if (activeTab !== 'ksef' || ksefDriversLoaded) return;
    (async () => {
      try {
        const snap = await get(ref(db, 'drivers'));
        if (snap.exists()) {
          const drivers = snap.val();
          const list = Object.entries(drivers).map(([id, d]) => ({
            id,
            name: d.name || d.displayName || id,
            email: d.email || '',
            akronim: d.akronim || '',
          })).sort((a, b) => a.name.localeCompare(b.name));
          setKsefDriversList(list);
        }
        setKsefDriversLoaded(true);
      } catch (e) {
        console.error('Error preloading drivers:', e);
      }
    })();
  }, [activeTab, db, ksefDriversLoaded]);

  // Build portal index: map various invoice keys -> { driverId, invoiceId, driverLabel, addedAt, source }
  const buildPortalIndex = useCallback((driversSnap) => {
    const index = new Map();
    if (!driversSnap?.exists()) return index;
    const drivers = driversSnap.val();
    for (const driverId in drivers) {
      const driver = drivers[driverId] || {};
      const driverLabel =
        driver.akronim ||
        [driver.imie, driver.nazwisko].filter(Boolean).join(' ').trim() ||
        driver.email ||
        driverId;
      const driverInvoices = driver.invoices;
      if (!driverInvoices) continue;
      for (const invId in driverInvoices) {
        const inv = driverInvoices[invId] || {};
        const info = {
          driverId,
          invoiceId: invId,
          driverLabel,
          addedAt: inv.timestamp || inv.uploadDate || inv.addedAt || inv.createdAt || null,
          source: inv.source || (inv.addedBy === 'admin' ? 'admin' : 'driver'),
        };
        // Strongest match: by KSeF number
        if (inv.ksefNumber) {
          index.set(`ksef:${String(inv.ksefNumber).trim()}`, info);
        }
        // Driver invoices use `numerfaktury` as primary; admin/transferred ones may also have `invoiceNumber`
        const num = String(inv.numerfaktury || inv.invoiceNumber || inv.numer || '').trim();
        const sellerNip = String(inv.nipseller || inv.sellerNip || '').trim().replace(/[\s-]/g, '');
        // Pair match: NIP + number
        if (num && sellerNip) {
          index.set(`pair:${sellerNip}|${num}`, info);
        }
        // Fallback: number only — keep first encountered
        if (num && !index.has(`num:${num}`)) {
          index.set(`num:${num}`, info);
        }
      }
    }
    return index;
  }, []);

  const matchPortalInvoice = useCallback((val, index) => {
    if (!index || index.size === 0) return null;
    const ksefNum = String(val.ksefNumber || '').trim();
    if (ksefNum && index.has(`ksef:${ksefNum}`)) return index.get(`ksef:${ksefNum}`);
    const num = String(val.invoiceNumber || '').trim();
    const sellerNip = String(val.sellerNip || '').trim().replace(/[\s-]/g, '');
    if (num && sellerNip && index.has(`pair:${sellerNip}|${num}`)) return index.get(`pair:${sellerNip}|${num}`);
    if (num && index.has(`num:${num}`)) return index.get(`num:${num}`);
    return null;
  }, []);

  // Live KSeF invoices listener + inPortal check
  useEffect(() => {
    if (activeTab !== 'ksef') return;
    setKsefLoading(true);

    const driversRef = ref(db, 'drivers');
    const invoicesRef = ref(db, 'ksefInvoices');
    let portalIndex = new Map();
    let cleanupInvoices = null;

    // Live listener for drivers — rebuild portalIndex when they change
    const unsubDrivers = onValue(driversRef, (driversSnap) => {
      portalIndex = buildPortalIndex(driversSnap);
      // (Re)attach KSeF listener with the fresh index
      if (cleanupInvoices) cleanupInvoices();
      const handler = onValue(invoicesRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const list = Object.entries(data).map(([key, val]) => {
            const portalInfo = matchPortalInvoice(val, portalIndex);
            return {
              key,
              ...val,
              inPortal: !!portalInfo,
              portalInfo,
            };
          }).sort((a, b) => new Date(b.issueDate || b.syncedAt) - new Date(a.issueDate || a.syncedAt));
          setKsefAllInvoices(list);
        } else {
          setKsefAllInvoices([]);
        }
        setKsefLoading(false);
      });
      cleanupInvoices = () => off(invoicesRef, 'value', handler);
    });

    return () => {
      off(driversRef, 'value', unsubDrivers);
      if (cleanupInvoices) cleanupInvoices();
    };
  }, [activeTab, db, buildPortalIndex, matchPortalInvoice]);

  // Filter KSeF invoices by search
  useEffect(() => {
    if (!ksefSearch.trim()) {
      setKsefInvoices(ksefAllInvoices);
      return;
    }
    const q = ksefSearch.toLowerCase();
    const filtered = ksefAllInvoices.filter(inv => {
      const searchFields = [
        inv.ksefNumber,
        inv.invoiceNumber,
        inv.sellerName,
        inv.sellerNip,
        inv.buyerName,
        inv.buyerNip,
        inv.vehicleNumber,
        inv.invoiceType,
        inv.invoicingMode,
        inv.currency,
        inv.systemInfo,
        inv.invoiceKind,
        inv.issueDate,
        inv.saleDate,
        inv.sellerAddress,
        inv.buyerAddress,
        inv.sellerPhone,
        String(inv.netAmount),
        String(inv.grossAmount),
        String(inv.vatAmount),
        // Line items
        ...(inv.lineItems || []).map(li => `${li.description} ${li.cnCode} ${li.gtu} ${li.unit}`),
        // Related entities
        ...(inv.relatedEntities || []).map(re => `${re.name} ${re.nip} ${re.address} ${re.role}`),
        // Additional info
        ...Object.values(inv.additionalInfo || {}),
        ...Object.keys(inv.additionalInfo || {}),
      ].filter(Boolean).join(' ').toLowerCase();
      return searchFields.includes(q);
    });
    setKsefInvoices(filtered);
  }, [ksefSearch, ksefAllInvoices]);

  // Listen to sync progress
  useEffect(() => {
    const progressRef = ref(db, 'ksefSyncProgress');
    const unsubProgress = onValue(progressRef, (snapshot) => {
      if (snapshot.exists()) {
        setSyncProgress(snapshot.val());
      } else {
        setSyncProgress(null);
      }
    });
    return () => off(progressRef);
  }, [db]);

  const loadKsefInvoices = async () => {
    setKsefLoading(true);
    try {
      const driversSnap = await get(ref(db, 'drivers'));
      const portalIndex = buildPortalIndex(driversSnap);

      const snap = await get(ref(db, 'ksefInvoices'));
      if (snap.exists()) {
        const data = snap.val();
        const list = Object.entries(data).map(([key, val]) => {
          const portalInfo = matchPortalInvoice(val, portalIndex);
          return {
            key,
            ...val,
            inPortal: !!portalInfo,
            portalInfo,
          };
        }).sort((a, b) => new Date(b.issueDate || b.syncedAt) - new Date(a.issueDate || a.syncedAt));
        setKsefInvoices(list);
      } else {
        setKsefInvoices([]);
      }
    } catch (err) {
      message.error('Błąd ładowania faktur KSeF: ' + err.message);
    }
    setKsefLoading(false);
  };

  const handleKsefSync = async () => {
    setKsefSyncLoading(true);
    try {
      const settingsSnap = await get(ref(db, 'ksefSettings'));
      if (!settingsSnap.exists()) {
        message.warning('Najpierw skonfiguruj KSeF w Ustawieniach → KSeF');
        setKsefSyncLoading(false);
        return;
      }
      const settings = settingsSnap.val();

      const functions = getFunctions(currentFirebase.app, 'europe-west1');
      const syncFn = httpsCallable(functions, 'ksefSync');

      // Sync only new invoices — use last sync date if available
      const lastSyncSnap = await get(ref(db, 'ksefSyncHistory'));
      let lastSyncDate = null;
      if (lastSyncSnap.exists()) {
        const history = lastSyncSnap.val();
        const entries = Object.values(history).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        if (entries.length > 0 && entries[0].timestamp) {
          lastSyncDate = entries[0].timestamp;
        }
      }
      const dateFrom = lastSyncDate || dayjs().subtract(30, 'day').toISOString();
      const dateTo = dayjs().toISOString();

      const result = await syncFn({
        nip: settings.nip,
        token: settings.token,
        environment: settings.environment || 'test',
        dateFrom,
        dateTo,
      });

      const data = result.data;
      message.success(`Synchronizacja: ${data.fetched} faktur, ${data.newInvoices} nowych`);
      loadKsefInvoices();
    } catch (err) {
      message.error('Błąd synchronizacji KSeF: ' + (err.message || err));
    }
    setKsefSyncLoading(false);
  };

  // Transfer KSeF invoice to driver portal with 50% VAT
  const handleKsefTransfer = async (values) => {
    if (!ksefTransferInvoice) return;
    setKsefTransferLoading(true);
    try {
      const inv = ksefTransferInvoice;
      const driverId = values.driverId;
      const vatPercent = values.vatPercent || 50;
      const parseAmt = (v) => parseFloat(String(v || '0').replace(',', '.')) || 0;
      const fmtAmt = (v) => v.toFixed(2).replace('.', ',');
      const grossNum = parseAmt(inv.grossAmount);
      const netNum = parseAmt(inv.netAmount);
      const vatNum = parseAmt(inv.vatAmount);
      const vatReturn = Number((vatNum * vatPercent / 100).toFixed(2));

      const invoiceData = {
        timestamp: Date.now(),
        invoiceNumber: inv.invoiceNumber || '',
        numerfaktury: inv.invoiceNumber || '',
        invoiceDate: inv.issueDate || '',
        purchaseDate: inv.issueDate || '',
        saleDate: inv.saleDate || '',
        grossAmount: fmtAmt(grossNum),
        netAmount: fmtAmt(netNum),
        vatAmount: fmtAmt(vatNum),
        vatPercent: vatPercent,
        vatReturn: fmtAmt(vatReturn),
        sellerName: inv.sellerName || '',
        sellerNip: inv.sellerNip || '',
        nipseller: inv.sellerNip || '',
        vehicleNumber: inv.vehicleNumber || values.vehicleNumber || '',
        registrationNumber: inv.vehicleNumber || values.vehicleNumber || '',
        status: 'W trakcie weryfikacji',
        addedBy: 'admin',
        source: 'ksef',
        ksefNumber: inv.ksefNumber || '',
        currency: inv.currency || 'PLN',
        type: 'faktura',
        uploadDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      };

      const invoicesRef = ref(db, `/drivers/${driverId}/invoices`);
      const newInvoiceRef = push(invoicesRef);
      await set(newInvoiceRef, invoiceData);

      message.success(`Faktura ${inv.invoiceNumber} przeniesiona do portalu (VAT ${vatPercent}%: ${vatReturn} zł)`);
      setKsefTransferModalVisible(false);
      setKsefTransferInvoice(null);
      loadKsefInvoices();
      loadAndDisplayData();
    } catch (error) {
      console.error('Error transferring KSeF invoice:', error);
      message.error('Błąd: ' + error.message);
    }
    setKsefTransferLoading(false);
  };

  const loadAndDisplayData = async () => {
    setLoading(true);
    try {
      // Используем Cloud Function — данные извлекаются на сервере,
      // клиент получает только фактуры (без weeks, личных данных и пр.)
      const functions = getFunctions(currentFirebase.app, 'europe-west1');
      const getInvoicesFn = httpsCallable(functions, 'getInvoicesList');
      const result = await getInvoicesFn();
      const { invoices: rawInvoices, statuses: statusArr, types: typeArr, drivers: driversArr } = result.data;

      // Добавляем addedDate для отображения
      const invoices = rawInvoices.map(inv => ({
        ...inv,
        addedDate: moment(inv.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      }));

      setStatuses(statusArr);
      setTypes(typeArr);
      setAllInvoicesData(invoices);
      setDriversData(driversArr);
      setCurrentPage(1);

      console.log(`Загружено ${invoices.length} фактур через Cloud Function`);

    } catch (error) {
      console.error('Error fetching invoices data: ', error);
      message.error('Błąd podczas pobierania danych.');
    }
    setLoading(false);
  };

  const renderSafeValue = (value) => {
    if (value === null || value === undefined) {
      return 'Nie określono';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'boolean') {
      return value ? 'Tak' : 'Nie';
    }
    return String(value);
  };

  // Обработчики для модального окна деталей фактуры
  const showInvoiceDetailsModal = (invoice) => {
    setSelectedInvoice(invoice);
    setIsInvoiceDetailsModalVisible(true);
  };

  const handleInvoiceDetailsModalCancel = () => {
    setIsInvoiceDetailsModalVisible(false);
    setSelectedInvoice(null);
    setIsEditMode(false);
    editForm.resetFields();
  };

  // Функция для включения режима редактирования
  const handleEditClick = () => {
    if (selectedInvoice) {
      // Заполняем форму текущими значениями
      const invoiceDateStr = selectedInvoice.invoiceDate || selectedInvoice.purchaseDate;
      const invoiceDateMoment = invoiceDateStr ? dayjs(invoiceDateStr, ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD.MM.YYYY']) : null;

      editForm.setFieldsValue({
        numerfaktury: selectedInvoice.numerfaktury,
        invoiceDate: invoiceDateMoment,
        type: selectedInvoice.type,
        fuelType: selectedInvoice.fuelType,
        nipseller: selectedInvoice.nipseller,
        registrationNumber: selectedInvoice.registrationNumber,
        grossAmount: selectedInvoice.grossAmount,
        netAmount: selectedInvoice.netAmount,
        vatAmount: selectedInvoice.vatAmount,
        vatRate: selectedInvoice.vatRate,
        vatReturn: selectedInvoice.vatReturn,
        liters: selectedInvoice.liters
      });
      setIsEditMode(true);
    }
  };

  // Функция для отмены редактирования
  const handleCancelEdit = () => {
    setIsEditMode(false);
    editForm.resetFields();
  };

  // Функция для сохранения изменений
  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      setIsSaving(true);

      // Преобразуем invoiceDate из dayjs в строку формата YYYY-MM-DD
      const processedValues = { ...values };
      if (processedValues.invoiceDate && dayjs.isDayjs(processedValues.invoiceDate)) {
        processedValues.invoiceDate = processedValues.invoiceDate.format('YYYY-MM-DD');
      }

      // Обновляем данные в Firebase
      const updates = {};
      Object.keys(processedValues).forEach(key => {
        if (processedValues[key] !== undefined && processedValues[key] !== null) {
          updates[`/drivers/${selectedInvoice.driverId}/invoices/${selectedInvoice.invoiceId}/${key}`] = processedValues[key];
        }
      });

      // Применяем все обновления
      for (const [path, value] of Object.entries(updates)) {
        await set(ref(db, path), value);
      }

      message.success('Zmiany zostały pomyślnie zapisane.');

      // Обновляем локальные данные (используем processedValues с форматированной датой)
      const updatedInvoice = { ...selectedInvoice, ...processedValues };

      setFilteredData(prevData =>
        prevData.map(invoice =>
          invoice.invoiceId === selectedInvoice.invoiceId
            ? updatedInvoice
            : invoice
        )
      );
      setInvoicesData(prevData =>
        prevData.map(invoice =>
          invoice.invoiceId === selectedInvoice.invoiceId
            ? updatedInvoice
            : invoice
        )
      );
      setSelectedInvoice(updatedInvoice);
      setIsEditMode(false);
      editForm.resetFields();

    } catch (error) {
      console.error('Error saving invoice:', error);
      message.error('Błąd podczas zapisywania zmian.');
    } finally {
      setIsSaving(false);
    }
  };

  // Функция обновления статуса в модальном окне
  const updateInvoiceStatusFromModal = async (newStatus) => {
    if (!selectedInvoice) return;

    try {
      const refPath = `/drivers/${selectedInvoice.driverId}/invoices/${selectedInvoice.invoiceId}/status`;
      await set(ref(db, refPath), newStatus);
      message.success('Status zaktualizowany pomyślnie.');

      // Обновляем локальные данные
      setFilteredData(prevData =>
        prevData.map(invoice =>
          invoice.invoiceId === selectedInvoice.invoiceId
            ? { ...invoice, status: newStatus }
            : invoice
        )
      );
      setInvoicesData(prevData =>
        prevData.map(invoice =>
          invoice.invoiceId === selectedInvoice.invoiceId
            ? { ...invoice, status: newStatus }
            : invoice
        )
      );

      // Обновляем выбранную фактуру
      setSelectedInvoice(prev => ({ ...prev, status: newStatus }));

    } catch (error) {
      console.error('Error updating status: ', error);
      message.error('Błąd podczas aktualizacji statusu.');
    }
  };

  // filterData оставлен для обратной совместимости (вызывается из handleKsefTransfer и др.)
  const filterData = () => {
    const lowerCaseSearchValue = searchValue.toLowerCase();
    let filtered = invoicesData.filter(invoice => {
      const matchesSearch = (
        (invoice.driverId?.toLowerCase().includes(lowerCaseSearchValue) || '') ||
        (invoice.numerfaktury?.toLowerCase().includes(lowerCaseSearchValue) || '') ||
        (invoice.nipseller?.toLowerCase().includes(lowerCaseSearchValue) || '') ||
        (invoice.rejectionComment?.toLowerCase().includes(lowerCaseSearchValue) || '')
      );

      const matchesStatus = status === 'all' || invoice.status === status;
      const matchesType = type === 'all' || invoice.type === type;

      // Убираем фильтрацию по дате здесь, так как она теперь происходит при загрузке
      return matchesSearch && matchesStatus && matchesType;
    });

    setFilteredData(filtered);
  };

  const updateInvoiceStatus = async (driverId, invoiceId, newStatus) => {
    try {
      const refPath = `/drivers/${driverId}/invoices/${invoiceId}/status`;
      await set(ref(db, refPath), newStatus);
      message.success('Status updated successfully.');
      setFilteredData(prevData =>
        prevData.map(invoice =>
          invoice.invoiceId === invoiceId ? { ...invoice, status: newStatus } : invoice
        )
      );
    } catch (error) {
      console.error('Error updating status: ', error);
      message.error('Błąd podczas aktualizacji statusu.');
    }
  };

  // Функция для получения статуса очереди задач
  const getTaskQueueStatus = async () => {
    setQueueLoading(true);
    try {
      // Получаем токен текущего пользователя
      const user = currentFirebase.auth.currentUser;
      if (!user) {
        throw new Error('Пользователь не авторизован');
      }

      const idToken = await user.getIdToken();

      // HTTP запрос к Cloud Function с авторизацией
      const response = await fetch('https://us-central1-ccmcolorpartner.cloudfunctions.net/getTaskQueueStatus', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          data: {}
        })
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        // Попробуем получить текст ошибки от сервера
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const result = await response.json();
      console.log('Queue data received:', result);
      setQueueData(result.result || result);
    } catch (error) {
      console.error('Error getting queue status:', error);
      message.error(`Błąd podczas pobierania statusu kolejki: ${error.message}`);
    } finally {
      setQueueLoading(false);
    }
  };

  // Обработчик открытия модального окна очереди
  const showQueueModal = () => {
    setIsQueueModalVisible(true);
    getTaskQueueStatus();
  };

  // Обработчик закрытия модального окна очереди
  const handleQueueModalCancel = () => {
    setIsQueueModalVisible(false);
    setQueueData(null);
  };

  // Обработчики для добавления фактур
  const showAddInvoiceModal = () => {
    setIsAddInvoiceModalVisible(true);
    addInvoiceForm.resetFields();
    setFileList([]);
  };

  const handleAddInvoiceModalCancel = () => {
    setIsAddInvoiceModalVisible(false);
    addInvoiceForm.resetFields();
    setFileList([]);
  };

  // Обработчик загрузки файла
  const handleFileUpload = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // Функция для добавления фактуры
  const handleAddInvoice = async (values) => {
    if (fileList.length === 0) {
      message.error('Пожалуйста, выберите файл');
      return;
    }

    setUploadLoading(true);
    try {
      const file = fileList[0].originFileObj;
      const driverId = values.driverId;
      const timestamp = Date.now();

      // Создаем уникальное имя файла
      const fileExtension = file.name.split('.').pop();
      const fileName = `${driverId}_${timestamp}.${fileExtension}`;

      // Загружаем файл в Firebase Storage в папку /invoice
      const fileStorageRef = storageRef(currentFirebase.storage, `invoice/${fileName}`);
      const uploadResult = await uploadBytes(fileStorageRef, file);

      // Получаем URL загруженного файла
      const fileURL = await getDownloadURL(uploadResult.ref);

      // Создаем данные фактуры для сохранения в базе
      const invoiceData = {
        timestamp: timestamp,
        status: 'W trakcie weryfikacji',
        addedBy: 'admin',
        fileURL: fileURL,
        fileName: fileName,
        originalFileName: file.name,
        fileSize: file.size,
        uploadDate: moment().format('YYYY-MM-DD HH:mm:ss')
      };

      // Сохраняем данные фактуры в Firebase Database
      const invoicesRef = ref(db, `/drivers/${driverId}/invoices`);
      const newInvoiceRef = push(invoicesRef);
      await set(newInvoiceRef, invoiceData);

      message.success('Faktura została pomyślnie dodana.!');
      handleAddInvoiceModalCancel();

      // Обновляем данные
      await loadAndDisplayData();

    } catch (error) {
      console.error('Error adding invoice:', error);
      message.error(`Błąd podczas dodawania faktury: ${error.message}`);
    } finally {
      setUploadLoading(false);
    }
  };

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleRowsPerPageChange = useCallback((current, size) => {
    setRowsPerPage(size);
    setCurrentPage(1);
  }, []);

  const showModal = (fileURL) => {
    setCurrentImage(fileURL);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setCurrentImage(null);
  };

  const handleDownload = async () => {
    if (currentImage) {
      setDownloading(true);
      try {
        const response = await fetch(currentImage);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');

        const invoice = filteredData.find(inv => inv.fileURL === currentImage);
        const fileName = `${invoice.numerfaktury}_${invoice.driverId}.png`;

        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      } catch (error) {
        message.error('Błąd podczas pobierania pliku.');
      } finally {
        setDownloading(false);
      }
    }
  };

  const parseAmount = (v) => parseFloat(String(v || '0').replace(',', '.')) || 0;

  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toFixed(2).replace('.', ',');
    }
    return value;
  };

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setSorterInfo(sorter);
    setCurrentPage(pagination.current);
  }, []);

  const paginatedData = useMemo(
    () => filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage),
    [filteredData, currentPage, rowsPerPage]
  );

  const getStatusClass = (status) => {
    if (status === 'zaakceptowany') return 'fk-status-accepted';
    if (status === 'odrzucony') return 'fk-status-rejected';
    if (status === 'W trakcie weryfikacji') return 'fk-status-pending';
    return 'fk-status-default';
  };

  const columns = useMemo(() => [
    {
      title: 'Data',
      dataIndex: 'addedDate',
      key: 'addedDate',
      sorter: (a, b) => a.timestamp - b.timestamp,
      width: 100,
      render: (date) => date ? (
        <span style={{ fontSize: 12, color: '#6b7280' }}>{moment(date).format('DD.MM.YY')}</span>
      ) : '-'
    },
    {
      title: 'Kierowca',
      dataIndex: 'driverId',
      key: 'driverId',
      sorter: (a, b) => (a.driverId || '').localeCompare(b.driverId || ''),
      width: 110,
      render: (id) => <span className="fk-mono">{id}</span>
    },
    {
      title: 'Numer',
      dataIndex: 'numerfaktury',
      key: 'numerfaktury',
      sorter: (a, b) => (a.numerfaktury || '').localeCompare(b.numerfaktury || ''),
      width: 140,
      render: (num, record) => (
        <span className="fk-mono">{num || record.invoiceId || '-'}</span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status) => (
        <span className={`fk-status ${getStatusClass(status)}`}>
          <span className="fk-status-dot" />
          {status || '-'}
        </span>
      )
    },
    {
      title: 'Źródło',
      dataIndex: 'source',
      key: 'source',
      width: 70,
      render: (source, record) => {
        if (source === 'ksef' || source === 'ksef_driver')
          return <span className="fk-source fk-source-ksef">KSeF</span>;
        if (record.addedBy === 'admin')
          return <span className="fk-source fk-source-admin">Admin</span>;
        return <span className="fk-source fk-source-driver">Kier.</span>;
      },
      filters: [
        { text: 'KSeF', value: 'ksef' },
        { text: 'Kierowca', value: 'driver' },
        { text: 'Admin', value: 'admin' },
      ],
      onFilter: (value, record) => {
        if (value === 'ksef') return record.source === 'ksef' || record.source === 'ksef_driver';
        if (value === 'admin') return record.addedBy === 'admin' && record.source !== 'ksef';
        return !record.addedBy || record.addedBy === 'driver';
      },
    },
    {
      title: 'Typ',
      dataIndex: 'type',
      key: 'type',
      sorter: (a, b) => (a.type || '').localeCompare(b.type || ''),
      width: 60,
      render: (type) => type ? <span className="fk-type">{formatDocumentType(type)}</span> : '-'
    },
    {
      title: 'NIP',
      dataIndex: 'nipseller',
      key: 'nipseller',
      sorter: (a, b) => (a.nipseller || '').localeCompare(b.nipseller || ''),
      width: 110,
      render: (nip) => nip ? <span className="fk-mono">{nip}</span> : '-'
    },
    {
      title: 'Brutto',
      dataIndex: 'grossAmount',
      key: 'grossAmount',
      sorter: (a, b) => parseAmount(a.grossAmount) - parseAmount(b.grossAmount),
      width: 100,
      align: 'right',
      render: (v) => v ? <span className="fk-amount fk-amount-primary">{formatNumber(v)}</span> : '-'
    },
    {
      title: 'Netto',
      dataIndex: 'netAmount',
      key: 'netAmount',
      sorter: (a, b) => parseAmount(a.netAmount) - parseAmount(b.netAmount),
      width: 90,
      align: 'right',
      render: (v) => v ? <span className="fk-amount">{formatNumber(v)}</span> : '-'
    },
    {
      title: 'VAT',
      dataIndex: 'vatAmount',
      key: 'vatAmount',
      sorter: (a, b) => parseAmount(a.vatAmount) - parseAmount(b.vatAmount),
      width: 90,
      align: 'right',
      render: (v) => v ? <span className="fk-amount fk-amount-green">{formatNumber(v)}</span> : '-'
    },
    {
      title: 'Zwrot',
      dataIndex: 'vatReturn',
      key: 'vatReturn',
      sorter: (a, b) => parseAmount(a.vatReturn) - parseAmount(b.vatReturn),
      width: 90,
      align: 'right',
      render: (v) => v ? <span className="fk-amount fk-amount-orange">{formatNumber(v)}</span> : '-'
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => showInvoiceDetailsModal(record)}
        >
          Szczegóły
        </Button>
      )
    }
  ], []);

  // Колонки для таблицы задач в очереди
  const queueTaskColumns = [
    {
      title: 'Nazwa zadania',
      dataIndex: 'name',
      key: 'name',
      render: (name) => name.split('/').pop()
    },
    {
      title: 'Czas utworzenia',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (time) => time ? moment(time.seconds * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: 'Zaplanowany czas',
      dataIndex: 'scheduleTime',
      key: 'scheduleTime',
      render: (time) => time ? moment(time.seconds * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: 'Liczba prób',
      dataIndex: 'dispatchCount',
      key: 'dispatchCount',
      render: (count) => count || 0
    }
  ];

  // KSeF columns — cleaned up per client request
  const ksefColumns = [
    {
      title: 'Akronim',
      dataIndex: 'relatedEntities',
      key: 'akronim',
      width: 100,
      fixed: 'left',
      render: (entities) => {
        const role11 = entities?.find(e => String(e.role) === '11');
        return role11 ? <Tag color="blue"><strong>{role11.name}</strong></Tag> : '-';
      },
      filters: (() => {
        const uniqueNames = [...new Set(ksefInvoices
          .map(inv => inv.relatedEntities?.find(e => String(e.role) === '11')?.name)
          .filter(Boolean))];
        return uniqueNames.map(name => ({ text: name, value: name }));
      })(),
      onFilter: (value, record) => {
        const role11 = record.relatedEntities?.find(e => String(e.role) === '11');
        return role11?.name === value;
      },
    },
    {
      title: 'Nr faktury',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 180,
    },
    {
      title: 'W portalu',
      dataIndex: 'inPortal',
      key: 'inPortal',
      width: 160,
      align: 'center',
      render: (val, record) => {
        if (!val) return <Tag color="red" style={{ margin: 0 }}>Brak</Tag>;
        const info = record.portalInfo;
        const tooltip = info ? (
          <div style={{ lineHeight: 1.6 }}>
            <div><strong>Kierowca:</strong> {info.driverLabel}</div>
            {info.addedAt && (
              <div><strong>Dodano:</strong> {dayjs(info.addedAt).format('DD.MM.YYYY HH:mm')}</div>
            )}
            {info.source && <div><strong>Źródło:</strong> {info.source}</div>}
          </div>
        ) : 'Dodana w portalu';
        return (
          <Tooltip title={tooltip}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Tag color="green" style={{ margin: 0 }}>Dodana</Tag>
              {info?.driverLabel && (
                <span style={{ fontSize: 10, color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {info.driverLabel}
                </span>
              )}
            </div>
          </Tooltip>
        );
      },
      filters: [
        { text: 'Dodana', value: true },
        { text: 'Brak', value: false },
      ],
      onFilter: (value, record) => record.inPortal === value,
    },
    {
      title: 'Data wystawienia',
      dataIndex: 'issueDate',
      key: 'issueDate',
      width: 120,
      render: (text) => text ? dayjs(text).format('DD.MM.YYYY') : '-',
      sorter: (a, b) => new Date(a.issueDate) - new Date(b.issueDate),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Sprzedawca',
      dataIndex: 'sellerName',
      key: 'sellerName',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'NIP sprzedawcy',
      dataIndex: 'sellerNip',
      key: 'sellerNip',
      width: 120,
    },
    {
      title: 'Nabywca',
      dataIndex: 'buyerName',
      key: 'buyerName',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'NIP nabywcy',
      dataIndex: 'buyerNip',
      key: 'buyerNip',
      width: 120,
    },
    {
      title: 'Netto',
      dataIndex: 'netAmount',
      key: 'netAmount',
      width: 120,
      align: 'right',
      render: (val) => val ? `${formatNumber(val)} zł` : '-',
      sorter: (a, b) => parseAmount(a.netAmount) - parseAmount(b.netAmount),
    },
    {
      title: 'VAT',
      dataIndex: 'vatAmount',
      key: 'vatAmount',
      width: 110,
      align: 'right',
      render: (val) => val ? `${formatNumber(val)} zł` : '-',
    },
    {
      title: 'Brutto',
      dataIndex: 'grossAmount',
      key: 'grossAmount',
      width: 120,
      align: 'right',
      render: (val) => val ? <strong>{formatNumber(val)} zł</strong> : '-',
      sorter: (a, b) => parseAmount(a.grossAmount) - parseAmount(b.grossAmount),
    },
    {
      title: 'Waluta',
      dataIndex: 'currency',
      key: 'currency',
      width: 70,
    },
    {
      title: 'Data sprzedaży',
      dataIndex: 'saleDate',
      key: 'saleDate',
      width: 120,
      render: (text) => text ? dayjs(text).format('DD.MM.YYYY') : '-',
      sorter: (a, b) => new Date(a.saleDate || 0) - new Date(b.saleDate || 0),
    },
    {
      title: 'Adres sprzedawcy',
      dataIndex: 'sellerAddress',
      key: 'sellerAddress',
      width: 200,
      ellipsis: true,
      render: (text, record) => [text, record.sellerAddress2].filter(Boolean).join(', ') || '-',
    },
    {
      title: 'Tel. sprzedawcy',
      dataIndex: 'sellerPhone',
      key: 'sellerPhone',
      width: 130,
    },
    {
      title: 'Adres nabywcy',
      dataIndex: 'buyerAddress',
      key: 'buyerAddress',
      width: 200,
      ellipsis: true,
      render: (text, record) => [text, record.buyerAddress2].filter(Boolean).join(', ') || '-',
    },
    {
      title: 'Nr pojazdu',
      dataIndex: 'vehicleNumber',
      key: 'vehicleNumber',
      width: 110,
      render: (val) => val ? <Tag color="geekblue">{val}</Tag> : '-',
    },
    {
      title: 'Podmioty powiązane',
      dataIndex: 'relatedEntities',
      key: 'relatedEntities',
      width: 300,
      render: (entities) => entities && entities.length > 0
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {entities.map((e, i) => (
              <Tooltip key={i} title={[e.name, e.nip ? `NIP: ${e.nip}` : null, e.address, `Rola: ${e.role}`].filter(Boolean).join(' | ')}>
                <Tag color="purple" style={{ margin: 0, whiteSpace: 'normal', lineHeight: 1.3 }}>
                  <strong>{e.name}</strong> (rola {e.role}){e.address ? <div style={{ fontSize: 11, opacity: 0.8 }}>{e.address}</div> : null}
                </Tag>
              </Tooltip>
            ))}
          </div>
        : '-',
    },
    {
      title: 'Pozycje',
      dataIndex: 'lineItemsCount',
      key: 'lineItemsCount',
      width: 70,
      align: 'center',
      render: (val) => val || '-',
    },
    {
      title: 'Towary/Usługi',
      dataIndex: 'lineItems',
      key: 'lineItems',
      width: 250,
      ellipsis: true,
      render: (items) => items && items.length > 0
        ? items.map(i => `${i.description} (${i.quantity} ${i.unit})`).join('; ')
        : '-',
    },
    {
      title: 'Płatność',
      dataIndex: 'payment',
      key: 'payment',
      width: 120,
      render: (p) => p ? (
        <Space direction="vertical" size={0}>
          <Tag color={p.isPaid ? 'green' : 'red'}>{p.isPaid ? 'Zapłacono' : 'Niezapłacono'}</Tag>
          {p.paymentDate && <span style={{ fontSize: 11 }}>{p.paymentDate}</span>}
        </Space>
      ) : '-',
    },
    {
      title: 'Rodzaj',
      dataIndex: 'invoiceKind',
      key: 'invoiceKind',
      width: 70,
      render: (val) => val ? <Tag>{val}</Tag> : '-',
    },
    {
      title: 'Samofaktura',
      dataIndex: 'isSelfInvoicing',
      key: 'isSelfInvoicing',
      width: 90,
      render: (val) => val ? <Tag color="red">Tak</Tag> : 'Nie',
    },
    {
      title: 'Pobrano',
      dataIndex: 'syncedAt',
      key: 'syncedAt',
      width: 140,
      render: (text) => text ? dayjs(text).format('DD.MM.YYYY HH:mm') : '-',
      sorter: (a, b) => new Date(a.syncedAt || 0) - new Date(b.syncedAt || 0),
    },
    {
      title: 'Akcje',
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Podgląd faktury">
            <Button
              size="small"
              icon={<Eye size={14} weight="bold" />}
              onClick={(e) => {
                e.stopPropagation();
                setKsefViewInvoice(record);
                setIsKsefViewModalVisible(true);
              }}
            />
          </Tooltip>
          {!record.inPortal && (
            <Button
              size="small"
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
                setKsefTransferInvoice(record);
                ksefTransferForm.resetFields();
                let matchedDriverId = null;
                const role11 = record.relatedEntities?.find(e => String(e.role) === '11');
                if (role11?.name) {
                  const match = ksefDriversList.find(d => d.akronim && d.akronim.toLowerCase() === role11.name.toLowerCase());
                  if (match) matchedDriverId = match.id;
                }
                ksefTransferForm.setFieldsValue({
                  vatPercent: 50,
                  vehicleNumber: record.vehicleNumber || '',
                  ...(matchedDriverId ? { driverId: matchedDriverId } : {}),
                });
                setKsefTransferModalVisible(true);
              }}
            >
              Przenieś
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Push tabs into header
  useEffect(() => {
    const tabs = [
      { key: 'faktury', label: 'Faktury', count: filteredData.length, active: activeTab === 'faktury', onClick: () => setActiveTab('faktury') },
    ];
    if (hasKsefAccess) {
      tabs.push({ key: 'ksef', label: 'KSeF', count: ksefInvoices.length || null, active: activeTab === 'ksef', onClick: () => setActiveTab('ksef') });
    }
    window.__headerTabs = tabs;
    // Force header re-render
    window.dispatchEvent(new Event('headerTabsChanged'));
    return () => { window.__headerTabs = null; window.dispatchEvent(new Event('headerTabsChanged')); };
  }, [activeTab, filteredData.length, ksefInvoices.length, hasKsefAccess]);

  return (
    <ConfigProvider locale={plPL}>
      <div>

        {activeTab === 'ksef' ? (
          <div>
            {/* KSeF toolbar */}
            <div className="faktury-toolbar">
              <Input
                prefix={<MagnifyingGlass size={16} color="#9ca3af" />}
                placeholder="Szukaj: nr faktury, NIP, nazwa, pojazd..."
                value={ksefSearch}
                onChange={(e) => setKsefSearch(e.target.value)}
                allowClear
                style={{ width: 340 }}
              />
              <div style={{ flex: 1 }} />
              <span className="faktury-stats">
                <span className="faktury-stats-count">{ksefInvoices.length}</span>
                {ksefSearch ? ` / ${ksefAllInvoices.length}` : ''} faktur
              </span>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadKsefInvoices}
                style={{ borderRadius: 8 }}
              >
                Odśwież
              </Button>
              <Button
                icon={<ReloadOutlined />}
                style={{ borderRadius: 8 }}
                onClick={async () => {
                  try {
                    message.loading('Dosync brakujących danych XML...', 0);
                    const functions = getFunctions(currentFirebase.app, 'europe-west1');
                    const resyncFn = httpsCallable(functions, 'ksefResync');
                    const result = await resyncFn();
                    message.destroy();
                    message.success(`Resync: ${result.data.updated} zaktualizowano z ${result.data.total} niekompletnych`);
                    loadKsefInvoices();
                  } catch (err) {
                    message.destroy();
                    message.error('Resync error: ' + err.message);
                  }
                }}
              >
                Uzupełnij XML
              </Button>
              <Button
                type="primary"
                icon={<SyncOutlined spin={ksefSyncLoading || (syncProgress?.status === 'downloading')} />}
                loading={ksefSyncLoading}
                onClick={handleKsefSync}
                disabled={syncProgress?.status === 'downloading'}
                style={{ borderRadius: 8 }}
              >
                Synchronizuj
              </Button>
            </div>

            {/* Sync Progress */}
            {syncProgress && syncProgress.status !== 'completed' && (
              <Card size="small" style={{ marginBottom: 16, borderColor: '#1890ff' }}>
                <Row gutter={16} align="middle">
                  <Col flex="auto">
                    <div style={{ marginBottom: 4 }}>
                      <SyncOutlined spin style={{ marginRight: 8, color: '#1890ff' }} />
                      <strong>{syncProgress.phase || 'Synchronizacja...'}</strong>
                    </div>
                    <div style={{
                      height: 6,
                      background: '#f0f0f0',
                      borderRadius: 3,
                      overflow: 'hidden',
                      marginBottom: 4
                    }}>
                      <div style={{
                        height: '100%',
                        width: syncProgress.total > 0 ? `${Math.round((syncProgress.downloaded / syncProgress.total) * 100)}%` : '0%',
                        background: 'linear-gradient(90deg, #1890ff, #52c41a)',
                        borderRadius: 3,
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <Space size="large" style={{ fontSize: 12, color: '#666' }}>
                      <span>Pobrano: <strong>{syncProgress.downloaded || 0}</strong> / {syncProgress.total || '?'}</span>
                      <span>Nowych: <strong style={{ color: '#52c41a' }}>{syncProgress.saved || 0}</strong></span>
                      {syncProgress.errors > 0 && <span>Błędy: <strong style={{ color: '#ff4d4f' }}>{syncProgress.errors}</strong></span>}
                      {syncProgress.currentInvoice && <span>Aktualna: <Tag size="small">{syncProgress.currentInvoice}</Tag></span>}
                    </Space>
                  </Col>
                </Row>
              </Card>
            )}

            {syncProgress?.status === 'completed' && (
              <Card size="small" style={{ marginBottom: 16, borderColor: '#52c41a', background: '#f6ffed' }}>
                <Space>
                  <Tag color="success">Zakończono</Tag>
                  <span>{syncProgress.phase}</span>
                  {syncProgress.completedAt && <span style={{ color: '#999', fontSize: 12 }}>{dayjs(syncProgress.completedAt).format('HH:mm:ss')}</span>}
                </Space>
              </Card>
            )}

            <Table
              columns={ksefColumns}
              dataSource={ksefInvoices}
              loading={ksefLoading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} z ${total} pozycji`,
              }}
              scroll={{ x: 3000 }}
              size="small"
              bordered
            />
          </div>
        ) : (
        <div>
        {/* Toolbar: single row */}
        <div className="faktury-toolbar">
          <Input
            prefix={<MagnifyingGlass size={16} color="#9ca3af" />}
            placeholder="Szukaj..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            allowClear
            style={{ flex: 2, minWidth: 150 }}
          />
          <Select value={status} onChange={(v) => setStatus(v)} style={{ flex: 1, minWidth: 130 }}>
            <Option value="all">Wszystkie statusy</Option>
            {statuses.map((s) => <Option key={s} value={s}>{s}</Option>)}
          </Select>
          <Select value={type} onChange={(v) => setType(v)} style={{ flex: 1, minWidth: 100 }}>
            <Option value="all">Wszystkie typy</Option>
            {types.map((t) => <Option key={t} value={t}>{formatDocumentType(t)}</Option>)}
          </Select>
          <RangePicker
            value={dateRange}
            onChange={(dates) => { setDateRange(dates || null); setCurrentPage(1); }}
            presets={rangePresets}
            allowClear
            format="DD.MM.YYYY"
            placeholder={['Od', 'Do']}
            style={{ flex: 1, minWidth: 250 }}
          />
          <Button type="primary" onClick={showAddInvoiceModal} style={{ flexShrink: 0 }}>
            <Plus size={15} weight="bold" /> Dodaj
          </Button>
        </div>

        {/* Table */}
        <Table
          className="faktury-table"
          columns={columns}
          dataSource={filteredData}
          pagination={{
            current: currentPage,
            pageSize: rowsPerPage,
            total: filteredData.length,
            onChange: handlePageChange,
            onShowSizeChange: handleRowsPerPageChange,
            showSizeChanger: true,
            pageSizeOptions: ['15', '25', '50'],
            showTotal: (total, range) => `${range[0]}–${range[1]} z ${total}`,
            size: 'small',
          }}
          rowKey="invoiceId"
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          size="middle"
        />

        {/* Модальное окно для просмотра файлов */}
        <Modal
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Podgląd Pliku</span>
            </div>
          }
          visible={isModalVisible}
          onCancel={handleCancel}
          footer={
            <Button onClick={handleDownload} disabled={!currentImage || downloading}>
              {downloading ? <Spin /> : 'Pobierz'}
            </Button>
          }
        >
          {currentImage && <img src={currentImage} alt="Invoice Preview" style={{ width: '100%' }} />}
        </Modal>

        {/* Модальное окно для добавления фактур */}
        <Modal
          title="Dodaj fakturę dla kierowcy"
          visible={isAddInvoiceModalVisible}
          onCancel={handleAddInvoiceModalCancel}
          footer={null}
          width={600}
        >
          <Form
            form={addInvoiceForm}
            layout="vertical"
            onFinish={handleAddInvoice}
          >
            <Form.Item
              label="Wybierz kierowcę"
              name="driverId"
              rules={[{ required: true, message: 'Proszę wybrać kierowcę!' }]}
            >
              <Select
                placeholder="Wybierz kierowcę"
                showSearch
                filterOption={(input, option) => {
                  const driver = driversData.find(d => d.id === option.value);
                  if (!driver) return false;
                  const searchText = `${driver.name} ${driver.id} ${driver.email || ''}`.toLowerCase();
                  return searchText.includes(input.toLowerCase());
                }}
              >
                {driversData.map(driver => (
                  <Option key={driver.id} value={driver.id}>
                    {driver.name} ({driver.id})
                    {driver.email && ` - ${driver.email}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Plik faktury"
              name="file"
              rules={[{ required: true, message: 'Proszę wybrać plik!' }]}
            >
              <Upload
                listType="picture"
                fileList={fileList}
                onChange={handleFileUpload}
                beforeUpload={() => false} // Предотвращаем автоматическую загрузку
                accept="image/*,.pdf"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Wybierz plik</Button>
              </Upload>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleAddInvoiceModalCancel}>
                  Anuluj
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={uploadLoading}
                  disabled={fileList.length === 0}
                >
                  Dodaj fakturę
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
        {/* CSS для анимаций */}
        <style jsx>{`
  @keyframes statusPulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }

  .info-card {
    transition: all 0.3s ease;
  }

  .info-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.1);
  }

  .image-preview {
    transition: all 0.3s ease;
    cursor: zoom-in;
  }

  .image-preview:hover {
    transform: scale(1.01); // Уменьшил с 1.02 до 1.01
    box-shadow: 0 8px 25px rgba(0,0,0,0.15); // Уменьшил тень
    filter: brightness(1.05); // Добавил легкое осветление вместо сильного увеличения
  }

  .status-indicator {
    animation: statusPulse 2s ease-in-out infinite;
    transition: all 0.3s ease;
  }

  .status-indicator:hover {
    animation-play-state: paused;
    transform: scale(1.1);
  }

  .button-animated {
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  .button-animated::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s;
  }

  .button-animated:hover::before {
    left: 100%;
  }

  .button-animated:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  }

  .scroll-area {
    scrollbar-width: thin;
    scrollbar-color: #d4d4d4 transparent;
  }

  .scroll-area::-webkit-scrollbar {
    width: 6px;
  }

  .scroll-area::-webkit-scrollbar-track {
    background: transparent;
  }

  .scroll-area::-webkit-scrollbar-thumb {
    background-color: #d4d4d4;
    border-radius: 3px;
    transition: background-color 0.3s ease;
  }

  .scroll-area::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }

  .loading-shimmer {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
  }

  .badge-animated {
    transition: all 0.3s ease;
    display: inline-block;
  }

  .badge-animated:hover {
    transform: scale(1.1);
  }

  .json-data {
    transition: all 0.3s ease;
  }

  .json-data:hover {
    background-color: #f8f8f8 !important;
  }

  /* Адаптивность для мобильных устройств */
  @media (max-width: 768px) {
    .image-preview:hover {
      transform: none; // Отключаем увеличение на мобильных
    }
  }

  /* Ограничения для очень больших экранов */
  @media (min-width: 1600px) {
    .modal-container {
      max-width: 1400px;
      margin: 0 auto;
    }
  }
`}</style>

        {/* Модальное окно для подробной информации о фактуре */}
        <Modal
          title={
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '4px 0'
            }}>
              <span style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f1f1f'
              }}>
                {renderSafeValue(selectedInvoice?.numerfaktury || selectedInvoice?.invoiceId)}
              </span>
            </div>
          }
          visible={isInvoiceDetailsModalVisible}
          onCancel={handleInvoiceDetailsModalCancel}
          footer={
            isEditMode ? [
              <Button
                key="cancel"
                onClick={handleCancelEdit}
                disabled={isSaving}
                size="large"
                style={{
                  borderRadius: '8px',
                  fontWeight: '500'
                }}
              >
                Anuluj
              </Button>,
              <Button
                key="save"
                type="primary"
                onClick={handleSaveEdit}
                loading={isSaving}
                size="large"
                style={{
                  borderRadius: '8px',
                  fontWeight: '500',
                  backgroundColor: '#1890ff'
                }}
              >
                Zapisz zmiany
              </Button>
            ] : [
              <Button
                key="edit"
                type="primary"
                onClick={handleEditClick}
                size="large"
                style={{
                  borderRadius: '8px',
                  fontWeight: '500',
                  backgroundColor: '#1890ff'
                }}
              >
                Edytuj
              </Button>,
              <Button
                key="close"
                onClick={handleInvoiceDetailsModalCancel}
                size="large"
                style={{
                  borderRadius: '8px',
                  fontWeight: '500'
                }}
              >
                Zamknij
              </Button>
            ]
          }
          width={1400}
          style={{
            top: 20,
            maxWidth: '95vw',
            maxHeight: '95vh'
          }}
          bodyStyle={{
            height: 'calc(90vh - 120px)',
            padding: 0,
            overflow: 'hidden'
          }}
        >
          {selectedInvoice && (
            <div style={{
              display: 'flex',
              height: '100%',
              gap: '0',
              overflow: 'hidden' // Ограничиваем основной контейнер
            }}>
              {/* Левая колонка - Изображение */}
              <div style={{
                flex: '0 0 50%',
                backgroundColor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid #e8e8e8',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {selectedInvoice.fileURL ? (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '15px',
                      overflow: 'hidden'
                    }}>
                      <img
                        src={selectedInvoice.fileURL}
                        alt="Invoice Preview"
                        className="image-preview"
                        style={{
                          maxWidth: 'calc(100% - 40px)',
                          maxHeight: 'calc(100% - 40px)',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          border: '1px solid #e8e8e8',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          cursor: 'zoom-in',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}
                        onClick={() => window.open(selectedInvoice.fileURL, '_blank')}
                      />
                    </div>
                    <div style={{
                      padding: '16px',
                      textAlign: 'center',
                      borderTop: '1px solid #f0f0f0',
                      backgroundColor: '#fafafa',
                      flexShrink: 0
                    }}>
                      <Button
                        onClick={() => window.open(selectedInvoice.fileURL, '_blank')}
                        type="default"
                        size="large"
                        style={{
                          borderRadius: '8px',
                          fontWeight: '500',
                          border: '1px solid #d9d9d9'
                        }}
                      >
                        Otwórz w nowym oknie
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    color: '#999',
                    fontSize: '16px'
                  }}>
                    <div style={{
                      fontSize: '48px',
                      marginBottom: '16px',
                      animation: 'statusPulse 3s ease-in-out infinite'
                    }}>📄</div>
                    <div style={{
                      opacity: 0.7,
                      transition: 'opacity 0.3s ease'
                    }}>Brak podglądu pliku</div>
                  </div>
                )}
              </div>

              {/* Правая колонка - Информация */}
              <div className="scroll-area" style={{
                flex: 1,
                overflowY: 'auto',
                padding: '24px',
                backgroundColor: '#fafafa'
              }}>
                {/* Основная информация */}
                <Card
                  title={<span style={{ fontSize: '14px', fontWeight: '600', color: '#262626' }}>Podstawowe informacje</span>}
                  className="info-card"
                  style={{
                    marginBottom: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e8e8e8',
                    boxShadow: 'none'
                  }}
                  headStyle={{
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #f0f0f0',
                    padding: '12px 16px',
                    minHeight: 'auto'
                  }}
                  bodyStyle={{
                    padding: '16px'
                  }}
                >
                  {isEditMode ? (
                    <Form
                      form={editForm}
                      layout="vertical"
                      size="middle"
                      style={{ padding: '8px 0' }}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>Numer faktury</span>}
                            name="numerfaktury"
                            style={{ marginBottom: '16px' }}
                          >
                            <Input
                              placeholder="Numer faktury"
                              style={{
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>Data faktury</span>}
                            name="invoiceDate"
                            style={{ marginBottom: '16px' }}
                          >
                            <DatePicker
                              format="YYYY-MM-DD"
                              placeholder="YYYY-MM-DD"
                              style={{
                                width: '100%',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>Typ</span>}
                            name="type"
                            style={{ marginBottom: '16px' }}
                          >
                            <Select
                              placeholder="Wybierz typ"
                              style={{ borderRadius: '6px' }}
                            >
                              <Option value="faktura">Faktura</Option>
                              <Option value="paragon">Paragon</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>Rodzaj paliwa</span>}
                            name="fuelType"
                            style={{ marginBottom: '16px' }}
                          >
                            <Select
                              placeholder="Wybierz rodzaj paliwa"
                              style={{ borderRadius: '6px' }}
                            >
                              <Option value="Diesel">Diesel</Option>
                              <Option value="Benzyna">Benzyna</Option>
                              <Option value="Benzyna 95">Benzyna 95</Option>
                              <Option value="Benzyna 98">Benzyna 98</Option>
                              <Option value="LPG">LPG</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>NIP Sprzedawcy</span>}
                            name="nipseller"
                            style={{ marginBottom: '16px' }}
                          >
                            <Input
                              placeholder="NIP Sprzedawcy"
                              style={{
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>Numer rejestracyjny</span>}
                            name="registrationNumber"
                            style={{ marginBottom: '16px' }}
                          >
                            <Input
                              placeholder="Numer rejestracyjny"
                              style={{
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  ) : (
                    <Descriptions column={2} bordered size="small">
                      <Descriptions.Item label="Driver ID">{renderSafeValue(selectedInvoice.driverId)}</Descriptions.Item>
                      <Descriptions.Item label="Data dodania">{renderSafeValue(selectedInvoice.addedDate)}</Descriptions.Item>
                      <Descriptions.Item label="Numer faktury">{renderSafeValue(selectedInvoice.numerfaktury || selectedInvoice.invoiceId)}</Descriptions.Item>
                      <Descriptions.Item label="Data faktury">{formatDate(selectedInvoice.invoiceDate || selectedInvoice.purchaseDate)}</Descriptions.Item>
                      <Descriptions.Item label="Typ">{formatDocumentType(selectedInvoice.type)}</Descriptions.Item>
                      <Descriptions.Item label="Rodzaj paliwa">{renderSafeValue(selectedInvoice.fuelType)}</Descriptions.Item>
                      <Descriptions.Item label="Document ID">{renderSafeValue(selectedInvoice.documentId)}</Descriptions.Item>
                      <Descriptions.Item label="Nazwa pliku">{renderSafeValue(selectedInvoice.filename || selectedInvoice.filename)}</Descriptions.Item>
                    </Descriptions>
                  )}
                </Card>

                {/* Красивое отображение статуса */}
                <Card
                  title={<span style={{ fontSize: '14px', fontWeight: '600', color: '#262626' }}>Status dokumentu</span>}
                  className="info-card"
                  style={{
                    marginBottom: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e8e8e8',
                    boxShadow: 'none'
                  }}
                  headStyle={{
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #f0f0f0',
                    padding: '12px 16px',
                    minHeight: 'auto'
                  }}
                  bodyStyle={{
                    padding: '16px'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    borderRadius: '6px',
                    backgroundColor: selectedInvoice.status === 'zaakceptowany'
                      ? '#f6ffed'
                      : selectedInvoice.status === 'odrzucony'
                        ? '#fff2f0'
                        : selectedInvoice.status === 'W trakcie weryfikacji'
                          ? '#fff7e6'
                          : '#fafafa',
                    border: selectedInvoice.status === 'zaakceptowany'
                      ? '1px solid #d9f7be'
                      : selectedInvoice.status === 'odrzucony'
                        ? '1px solid #ffccc7'
                        : selectedInvoice.status === 'W trakcie weryfikacji'
                          ? '1px solid #ffe7ba'
                          : '1px solid #e8e8e8',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div className="status-indicator" style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: selectedInvoice.status === 'zaakceptowany'
                          ? '#52c41a'
                          : selectedInvoice.status === 'odrzucony'
                            ? '#ff4d4f'
                            : selectedInvoice.status === 'W trakcie weryfikacji'
                              ? '#fa8c16'
                              : '#d9d9d9'
                      }}></div>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: selectedInvoice.status === 'zaakceptowany'
                          ? '#52c41a'
                          : selectedInvoice.status === 'odrzucony'
                            ? '#ff4d4f'
                            : selectedInvoice.status === 'W trakcie weryfikacji'
                              ? '#fa8c16'
                              : '#666',
                        transition: 'color 0.3s ease'
                      }}>
                        {selectedInvoice.status === 'zaakceptowany'
                          ? '✓ Zaakceptowany'
                          : selectedInvoice.status === 'odrzucony'
                            ? '✗ Odrzucony'
                            : selectedInvoice.status === 'W trakcie weryfikacji'
                              ? '⏳ W trakcie weryfikacji'
                              : selectedInvoice.status || 'Nie określono'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>Zmień status:</span>
                      <Select
                        value={selectedInvoice?.status}
                        onChange={updateInvoiceStatusFromModal}
                        style={{
                          minWidth: '150px',
                          transition: 'all 0.3s ease'
                        }}
                        size="small"
                      >
                        <Option value="W trakcie weryfikacji">W trakcie weryfikacji</Option>
                        <Option value="zaakceptowany">Zaakceptowany</Option>
                        <Option value="odrzucony">Odrzucony</Option>
                      </Select>
                    </div>
                  </div>
                </Card>

                {/* Informacje o sprzedawcy */}
                <Card
                  title={<span style={{ fontSize: '14px', fontWeight: '600', color: '#262626' }}>Dane sprzedawcy</span>}
                  className="info-card"
                  style={{
                    marginBottom: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e8e8e8',
                    boxShadow: 'none'
                  }}
                  headStyle={{
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #f0f0f0',
                    padding: '12px 16px',
                    minHeight: 'auto'
                  }}
                  bodyStyle={{
                    padding: '16px'
                  }}
                >
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="NIP Sprzedawcy">{renderSafeValue(selectedInvoice.nipseller)}</Descriptions.Item>
                    <Descriptions.Item label="Numer rejestracyjny">{renderSafeValue(selectedInvoice.registrationNumber)}</Descriptions.Item>
                    {/* Обработка вложенного объекта vendor */}
                    {selectedInvoice.vendor && (
                      <Descriptions.Item label="Vendor">
                        <pre className="json-data" style={{ fontSize: '12px', margin: 0 }}>{JSON.stringify(selectedInvoice.vendor, null, 2)}</pre>
                      </Descriptions.Item>
                    )}
                    {selectedInvoice.vendorNIP && (
                      <Descriptions.Item label="Vendor NIP">{renderSafeValue(selectedInvoice.vendorNIP)}</Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>

                {/* Informacje o kupującym */}
                {(selectedInvoice.customerName || selectedInvoice.customerNIP || selectedInvoice.customerAddress || selectedInvoice.customer) && (
                  <Card
                    title={<span style={{ fontSize: '14px', fontWeight: '600', color: '#262626' }}>Dane kupującego</span>}
                    className="info-card"
                    style={{
                      marginBottom: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e8e8e8',
                      boxShadow: 'none'
                    }}
                    headStyle={{
                      backgroundColor: '#fff',
                      borderBottom: '1px solid #f0f0f0',
                      padding: '12px 16px',
                      minHeight: 'auto'
                    }}
                    bodyStyle={{
                      padding: '16px'
                    }}
                  >
                    <Descriptions column={1} bordered size="small">
                      {selectedInvoice.customerName && (
                        <Descriptions.Item label="Nazwa">{renderSafeValue(selectedInvoice.customerName)}</Descriptions.Item>
                      )}
                      {selectedInvoice.customerNIP && (
                        <Descriptions.Item label="NIP">{renderSafeValue(selectedInvoice.customerNIP)}</Descriptions.Item>
                      )}
                      {selectedInvoice.customerAddress && (
                        <Descriptions.Item label="Adres">{renderSafeValue(selectedInvoice.customerAddress)}</Descriptions.Item>
                      )}
                      {/* Обработка вложенного объекта customer */}
                      {selectedInvoice.customer && (
                        <Descriptions.Item label="Customer">
                          <pre className="json-data" style={{ fontSize: '12px', margin: 0 }}>{JSON.stringify(selectedInvoice.customer, null, 2)}</pre>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                )}

                {/* Kwoty finansowe */}
                <Card
                  title={<span style={{ fontSize: '14px', fontWeight: '600', color: '#262626' }}>Kwoty</span>}
                  className="info-card"
                  style={{
                    marginBottom: '16px',
                    borderRadius: '8px',
                    border: '1px solid #e8e8e8',
                    boxShadow: 'none'
                  }}
                  headStyle={{
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #f0f0f0',
                    padding: '12px 16px',
                    minHeight: 'auto'
                  }}
                  bodyStyle={{
                    padding: '16px'
                  }}
                >
                  {isEditMode ? (
                    <Form
                      form={editForm}
                      layout="vertical"
                      size="middle"
                      style={{ padding: '8px 0' }}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>Kwota brutto</span>}
                            name="grossAmount"
                            style={{ marginBottom: '16px' }}
                          >
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              suffix="zł"
                              style={{
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>Kwota netto</span>}
                            name="netAmount"
                            style={{ marginBottom: '16px' }}
                          >
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              suffix="zł"
                              style={{
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>Kwota VAT</span>}
                            name="vatAmount"
                            style={{ marginBottom: '16px' }}
                          >
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              suffix="zł"
                              style={{
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>Stawka VAT</span>}
                            name="vatRate"
                            style={{ marginBottom: '16px' }}
                          >
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              suffix="%"
                              style={{
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>VAT do zwrotu</span>}
                            name="vatReturn"
                            style={{ marginBottom: '16px' }}
                          >
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              suffix="zł"
                              style={{
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={<span style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>Litry</span>}
                            name="liters"
                            style={{ marginBottom: '16px' }}
                          >
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              style={{
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  ) : (
                    <Descriptions column={2} bordered size="small">
                      <Descriptions.Item label="Kwota brutto">
                        <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                          {formatNumber(selectedInvoice.grossAmount)} zł
                        </span>
                      </Descriptions.Item>
                      <Descriptions.Item label="Kwota netto">
                        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                          {formatNumber(selectedInvoice.netAmount)} zł
                        </span>
                      </Descriptions.Item>
                      <Descriptions.Item label="Kwota VAT">
                        <span style={{ fontWeight: 'bold', color: '#fa8c16' }}>
                          {formatNumber(selectedInvoice.vatAmount)} zł
                        </span>
                      </Descriptions.Item>
                      <Descriptions.Item label="Stawka VAT">{formatNumber(selectedInvoice.vatRate)}%</Descriptions.Item>
                      <Descriptions.Item label="VAT do zwrotu">
                        <span style={{ fontWeight: 'bold', color: '#722ed1' }}>
                          {formatNumber(selectedInvoice.vatReturn)} zł
                        </span>
                      </Descriptions.Item>
                      <Descriptions.Item label="Litry">
                        <span style={{ fontWeight: 'bold', color: '#13c2c2' }}>
                          {formatNumber(selectedInvoice.liters)}
                        </span>
                      </Descriptions.Item>
                      {/* Обработка totalAmount если это объект */}
                      {selectedInvoice.totalAmount && (
                        <Descriptions.Item label="Total Amount">
                          {typeof selectedInvoice.totalAmount === 'object'
                            ? <pre className="json-data" style={{ fontSize: '12px', margin: 0 }}>{JSON.stringify(selectedInvoice.totalAmount, null, 2)}</pre>
                            : <span style={{ fontWeight: 'bold', color: '#eb2f96' }}>
                              {formatNumber(selectedInvoice.totalAmount)} zł
                            </span>
                          }
                        </Descriptions.Item>
                      )}
                      {selectedInvoice.itemsCount && (
                        <Descriptions.Item label="Liczba pozycji">{renderSafeValue(selectedInvoice.itemsCount)}</Descriptions.Item>
                      )}
                    </Descriptions>
                  )}
                </Card>

                {/* Dodatkowe informacje */}
                {(selectedInvoice.additionalInfo || selectedInvoice.rejectionComment || selectedInvoice.rejectionSummary) && (
                  <Card
                    title={<span style={{ fontSize: '14px', fontWeight: '600', color: '#262626' }}>Dodatkowe informacje</span>}
                    className="info-card"
                    style={{
                      marginBottom: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e8e8e8',
                      boxShadow: 'none'
                    }}
                    headStyle={{
                      backgroundColor: '#fff',
                      borderBottom: '1px solid #f0f0f0',
                      padding: '12px 16px',
                      minHeight: 'auto'
                    }}
                    bodyStyle={{
                      padding: '16px'
                    }}
                  >
                    <Descriptions column={1} bordered size="small">
                      {selectedInvoice.additionalInfo && (
                        <Descriptions.Item label="Dodatkowe informacje">{renderSafeValue(selectedInvoice.additionalInfo)}</Descriptions.Item>
                      )}
                      {selectedInvoice.rejectionComment && (
                        <Descriptions.Item label="Komentarz odrzucenia">{renderSafeValue(selectedInvoice.rejectionComment)}</Descriptions.Item>
                      )}
                      {selectedInvoice.rejectionSummary && (
                        <Descriptions.Item label="Podsumowanie odrzucenia">
                          <div style={{
                            backgroundColor: '#fff2f0',
                            border: '1px solid #ffccc7',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            fontSize: '12px',
                            color: '#ff4d4f',
                            fontWeight: '500',
                            animation: 'statusPulse 3s ease-in-out infinite'
                          }}>
                            {renderSafeValue(selectedInvoice.rejectionSummary)}
                          </div>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                )}

                {/* Pozycje faktury (jeśli dostępne) */}
                {selectedInvoice.items && Array.isArray(selectedInvoice.items) && selectedInvoice.items.length > 0 && (
                  <Card
                    title={<span style={{ fontSize: '14px', fontWeight: '600', color: '#262626' }}>Pozycje na fakturze</span>}
                    className="info-card"
                    style={{
                      marginBottom: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e8e8e8',
                      boxShadow: 'none'
                    }}
                    headStyle={{
                      backgroundColor: '#fff',
                      borderBottom: '1px solid #f0f0f0',
                      padding: '12px 16px',
                      minHeight: 'auto'
                    }}
                    bodyStyle={{
                      padding: '16px'
                    }}
                  >
                    <Table
                      dataSource={selectedInvoice.items}
                      columns={[
                        { title: 'ID', dataIndex: 'id', key: 'id', width: 50 },
                        { title: 'Opis', dataIndex: 'description', key: 'description', render: (value) => renderSafeValue(value) },
                        { title: 'Kwota', dataIndex: 'amount', key: 'amount', render: (value) => `${renderSafeValue(value)} zł` }
                      ]}
                      pagination={false}
                      size="small"
                      rowKey={(record, index) => record.id || index}
                      style={{
                        background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)'
                      }}
                    />
                  </Card>
                )}

                {/* Informacje OCR i flagi */}
                {(selectedInvoice.ocrConfidence !== undefined ||
                  selectedInvoice.isImageReadable !== undefined ||
                  selectedInvoice.isBuyerColorPartner !== undefined ||
                  selectedInvoice.isBuyerAddressGrabiszynska !== undefined ||
                  selectedInvoice.isOnlyFuel !== undefined) && (
                    <Card
                      title={<span style={{ fontSize: '14px', fontWeight: '600', color: '#262626' }}>Informacje techniczne</span>}
                      className="info-card"
                      style={{
                        marginBottom: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e8e8e8',
                        boxShadow: 'none'
                      }}
                      headStyle={{
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #f0f0f0',
                        padding: '12px 16px',
                        minHeight: 'auto'
                      }}
                      bodyStyle={{
                        padding: '16px'
                      }}
                    >
                      <Descriptions column={2} bordered size="small">
                        {selectedInvoice.ocrConfidence !== undefined && typeof selectedInvoice.ocrConfidence !== 'object' && (
                          <Descriptions.Item label="Pewność OCR">{renderSafeValue(selectedInvoice.ocrConfidence)}</Descriptions.Item>
                        )}
                        {selectedInvoice.isImageReadable !== undefined && (
                          <Descriptions.Item label="Obraz czytelny">
                            <Badge
                              className="badge-animated"
                              status={selectedInvoice.isImageReadable ? 'success' : 'error'}
                              text={selectedInvoice.isImageReadable ? 'Tak' : 'Nie'}
                            />
                          </Descriptions.Item>
                        )}
                        {selectedInvoice.isBuyerColorPartner !== undefined && (
                          <Descriptions.Item label="Kupujący Color Partner">
                            <Badge
                              className="badge-animated"
                              status={selectedInvoice.isBuyerColorPartner ? 'success' : 'error'}
                              text={selectedInvoice.isBuyerColorPartner ? 'Tak' : 'Nie'}
                            />
                          </Descriptions.Item>
                        )}
                        {selectedInvoice.isBuyerAddressGrabiszynska !== undefined && (
                          <Descriptions.Item label="Adres Grabiszyńska">
                            <Badge
                              className="badge-animated"
                              status={selectedInvoice.isBuyerAddressGrabiszynska ? 'success' : 'error'}
                              text={selectedInvoice.isBuyerAddressGrabiszynska ? 'Tak' : 'Nie'}
                            />
                          </Descriptions.Item>
                        )}
                        {selectedInvoice.isOnlyFuel !== undefined && (
                          <Descriptions.Item label="Tylko paliwo">
                            <Badge
                              className="badge-animated"
                              status={selectedInvoice.isOnlyFuel ? 'success' : 'error'}
                              text={selectedInvoice.isOnlyFuel ? 'Tak' : 'Nie'}
                            />
                          </Descriptions.Item>
                        )}
                        {selectedInvoice.isDraft !== undefined && (
                          <Descriptions.Item label="Wersja robocza">
                            <Badge
                              className="badge-animated"
                              status={selectedInvoice.isDraft ? 'warning' : 'success'}
                              text={selectedInvoice.isDraft ? 'Tak' : 'Nie'}
                            />
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Card>
                  )}

                {/* OCR Data - если есть сложные данные */}
                {selectedInvoice.ocrData && (
                  <Card
                    title={<span style={{ fontSize: '14px', fontWeight: '600', color: '#262626' }}>Dane OCR</span>}
                    className="info-card"
                    style={{
                      marginBottom: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e8e8e8',
                      boxShadow: 'none'
                    }}
                    headStyle={{
                      backgroundColor: '#fff',
                      borderBottom: '1px solid #f0f0f0',
                      padding: '12px 16px',
                      minHeight: 'auto'
                    }}
                    bodyStyle={{
                      padding: '16px'
                    }}
                  >
                    <pre className="json-data" style={{
                      fontSize: '12px',
                      backgroundColor: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(selectedInvoice.ocrData, null, 2)}
                    </pre>
                  </Card>
                )}
              </div>
            </div>
          )}
        </Modal>
        {/* Модальное окно для очереди задач */}
        <Modal
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Status kolejki zadań</span>
              <Button
                icon={<ReloadOutlined />}
                onClick={getTaskQueueStatus}
                loading={queueLoading}
                size="small"
              >
                Odśwież
              </Button>
            </div>
          }
          visible={isQueueModalVisible}
          onCancel={handleQueueModalCancel}
          footer={[
            <Button key="close" onClick={handleQueueModalCancel}>
              Zamknij
            </Button>
          ]}
          width={1000}
        >
          {queueLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>Pobieranie danych kolejki...</div>
            </div>
          ) : queueData ? (
            <div>
              <Card title="Informacje o kolejce" style={{ marginBottom: '16px' }}>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Nazwa kolejki">
                    {queueData.queue.name.split('/').pop()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Badge
                      status={queueData.queue.state === 'RUNNING' ? 'processing' : 'default'}
                      text={queueData.queue.state}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Maksymalna liczba zadań na sekundę">
                    {queueData.queue.rateLimits?.maxDispatchesPerSecond || 'Nie ustawiono'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Maksymalna liczba równoczesnych zadań">
                    {queueData.queue.rateLimits?.maxConcurrentDispatches || 'Nie ustawiono'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Maksymalna liczba prób">
                    {queueData.queue.retryConfig?.maxAttempts || 'Nie ustawiono'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Całkowita liczba zadań">
                    <Badge count={queueData.tasks.total} style={{ backgroundColor: '#52c41a' }} />
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card title="Lista zadań w kolejce">
                {queueData.tasks.total > 0 ? (
                  <Table
                    columns={queueTaskColumns}
                    dataSource={queueData.tasks.tasks}
                    rowKey="name"
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: false
                    }}
                    size="small"
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    Brak zadań w kolejce
                  </div>
                )}
              </Card>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
              Błąd podczas pobierania danych kolejki
            </div>
          )}
        </Modal>
      </div>
        )}
      </div>
      {/* KSeF Transfer Modal */}
      <Modal
        title="Przenieś fakturę KSeF do portalu"
        open={ksefTransferModalVisible}
        onCancel={() => { setKsefTransferModalVisible(false); setKsefTransferInvoice(null); }}
        footer={null}
        width={500}
      >
        {ksefTransferInvoice && (
          <div>
            <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Nr faktury">{ksefTransferInvoice.invoiceNumber}</Descriptions.Item>
              <Descriptions.Item label="Sprzedawca">{ksefTransferInvoice.sellerName}</Descriptions.Item>
              <Descriptions.Item label="Brutto"><strong>{formatNumber(ksefTransferInvoice.grossAmount)} zł</strong></Descriptions.Item>
              <Descriptions.Item label="VAT">{formatNumber(ksefTransferInvoice.vatAmount)} zł</Descriptions.Item>
              <Descriptions.Item label="Nr pojazdu">{ksefTransferInvoice.vehicleNumber || '-'}</Descriptions.Item>
            </Descriptions>
            <Form
              form={ksefTransferForm}
              layout="vertical"
              onFinish={handleKsefTransfer}
            >
              <Form.Item
                name="driverId"
                label="Kierowca"
                rules={[{ required: true, message: 'Wybierz kierowcę' }]}
              >
                <Select
                  showSearch
                  placeholder="Wybierz kierowcę"
                  optionFilterProp="label"
                  options={ksefDriversList.map(d => ({
                    value: d.id,
                    label: `${d.name}${d.akronim ? ' [' + d.akronim + ']' : ''} (${d.email})`,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="vatPercent"
                label="% VAT do zwrotu"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value={50}>50% VAT</Option>
                  <Option value={100}>100% VAT</Option>
                  <Option value={0}>0% (bez zwrotu)</Option>
                </Select>
              </Form.Item>
              <Form.Item name="vehicleNumber" label="Nr rejestracyjny">
                <Input placeholder="np. WA 12345" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={ksefTransferLoading} block>
                  Przenieś do rozliczenia
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      <KsefInvoiceView
        invoice={ksefViewInvoice}
        open={isKsefViewModalVisible}
        onClose={() => setIsKsefViewModalVisible(false)}
      />
    </ConfigProvider>
  );
};

export default Faktury;